"use client";

// Panel que aparece abajo cuando alguien ya ingresó y todavía no activó los
// avisos ni instaló la app.
//
// Por qué existe: las notificaciones push estaban construidas de punta a punta
// pero con CERO suscripciones, porque el interruptor vivía escondido en el
// perfil y la app nunca lo ofrecía.
//
// Dos reglas que no se pueden romper:
//  1. Nunca pedir el permiso del navegador al cargar la página. El permiso se
//     concede UNA sola vez en la vida: si lo pedís sin contexto te lo niegan y
//     ya no hay forma de volver a preguntar. Primero explicamos para qué sirve
//     con nuestra propia tarjeta, y recién al tocar "Activar" se lo pedimos.
//  2. En iPhone el push solo funciona si la app está instalada en el inicio.
//     Por eso ahí se ofrece primero instalar y después los avisos.
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import Icon from "./Icon";
import { api, hasSession } from "../lib/api";

const CLAVE_POSPUESTO = "busco.onboarding.pospuesto";
const DIAS_ANTES_DE_REINSISTIR = 5;

// Evento propietario de Chrome/Edge: no está en lib.dom
interface PromptDeInstalacion extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function esIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ se presenta como Macintosh; se lo delata el táctil
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function yaInstalada() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export default function InstalarYAvisos() {
  const [visible, setVisible] = useState(false);
  const [instalable, setInstalable] = useState<PromptDeInstalacion | null>(null);
  const [pushActivo, setPushActivo] = useState<boolean | null>(null);
  const [instalada, setInstalada] = useState(true);
  const [ios, setIos] = useState(false);
  const [pasos, setPasos] = useState(false); // instrucciones manuales de iPhone
  const [error, setError] = useState("");
  const [listo, setListo] = useState<"push" | null>(null);

  // Captura el prompt de instalación apenas el navegador lo ofrece. Hay que
  // hacerlo antes de cualquier await o el evento se pierde.
  useEffect(() => {
    const capturar = (e: Event) => {
      e.preventDefault();
      setInstalable(e as PromptDeInstalacion);
    };
    window.addEventListener("beforeinstallprompt", capturar);
    const instalado = () => setInstalada(true);
    window.addEventListener("appinstalled", instalado);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturar);
      window.removeEventListener("appinstalled", instalado);
    };
  }, []);

  useEffect(() => {
    if (!hasSession()) return;

    const pospuesto = Number(localStorage.getItem(CLAVE_POSPUESTO) || 0);
    if (pospuesto && Date.now() - pospuesto < DIAS_ANTES_DE_REINSISTIR * 86400_000) return;

    setIos(esIOS());
    setInstalada(yaInstalada());

    const soportaPush = "serviceWorker" in navigator && "PushManager" in window;
    if (!soportaPush) {
      setPushActivo(true); // no hay nada que ofrecer por este lado
    } else {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPushActivo(!!sub))
        .catch(() => setPushActivo(true));
    }

    // Un respiro antes de aparecer: que la pantalla termine de cargar primero
    const t = setTimeout(() => setVisible(true), 2200);
    return () => clearTimeout(t);
  }, []);

  const posponer = useCallback(() => {
    localStorage.setItem(CLAVE_POSPUESTO, String(Date.now()));
    setVisible(false);
  }, []);

  async function activarAvisos() {
    setError("");
    try {
      const permiso = await Notification.requestPermission();
      if (permiso !== "granted") {
        setError(
          permiso === "denied"
            ? "Quedaron bloqueadas. Se habilitan desde el candado, al lado de la dirección."
            : "No se concedió el permiso.",
        );
        return;
      }
      const key = process.env.NEXT_PUBLIC_VAPID_KEY;
      if (!key) { setError("Los avisos no están configurados en el servidor."); return; }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
      const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      try {
        await api("/notifications/subscribe", {
          method: "POST",
          body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
        });
      } catch (e) {
        // Si el servidor no la guardó hay que deshacer la del navegador, o el
        // usuario cree que están activas y no le llega nada nunca.
        await sub.unsubscribe().catch(() => {});
        throw e;
      }
      setPushActivo(true);
      setListo("push");
      setTimeout(() => setVisible(false), 2600);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron activar.");
    }
  }

  async function instalar() {
    if (!instalable) { setPasos(true); return; }
    try {
      await instalable.prompt();
      const { outcome } = await instalable.userChoice;
      if (outcome === "accepted") setInstalada(true);
      setInstalable(null);
    } catch {
      setPasos(true);
    }
  }

  // Se ofrece instalar si no está instalada Y (el navegador nos dio el prompt
  // O es un iPhone, donde el prompt no existe pero sí se puede hacer a mano)
  const ofrecerInstalar = !instalada && (!!instalable || ios);
  const ofrecerAvisos = pushActivo === false && (!ios || instalada);

  if (!visible || pushActivo === null) return null;
  if (!ofrecerInstalar && !ofrecerAvisos && !listo) return null;

  return (
    <div
      role="dialog"
      aria-label="Configurar Busco"
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 pointer-events-none"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="pointer-events-auto mx-auto w-full max-w-lg rounded-2xl border border-white/12 bg-[#1A2123] shadow-[0_-8px_40px_rgba(0,0,0,0.5)] overflow-hidden animate-[subir_.28s_cubic-bezier(.2,.8,.3,1)]">
        <style>{`@keyframes subir{from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}}
          @media (prefers-reduced-motion:reduce){.animate-\\[subir_.28s_cubic-bezier\\(.2\\,.8\\,.3\\,1\\)\\]{animation:none}}`}</style>

        {listo === "push" ? (
          <div className="flex items-center gap-3 p-4">
            <span className="w-10 h-10 rounded-xl bg-[#5CBE8F]/15 text-[#5CBE8F] grid place-items-center flex-none">
              <Icon name="check-circle" size={20} />
            </span>
            <div>
              <div className="font-bold text-[15px]">Listo, ya te vamos a avisar</div>
              <div className="text-[13px] text-[#B5C0BB]">
                Te llega al teléfono cada vez que se mueva una reparación.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3 px-4 pt-4 pb-1">
              <div className="flex-1">
                <div className="font-bold text-[15.5px] tracking-tight">Terminá de configurar Busco</div>
                <div className="text-[13px] text-[#B5C0BB] mt-0.5">
                  Dos toques y no se te escapa ninguna reparación.
                </div>
              </div>
              <button
                onClick={posponer}
                aria-label="Ahora no"
                className="w-8 h-8 -mr-1 -mt-1 rounded-lg text-[#808D88] hover:bg-white/6 hover:text-[#EDF1EF] grid place-items-center flex-none"
              >
                <Icon name="x" size={17} />
              </button>
            </div>

            <div className="p-3 pt-2 flex flex-col gap-2">
              {ofrecerAvisos && (
                <Fila
                  icono="bell"
                  titulo="Activar los avisos"
                  detalle="Cuando aceptan tu presupuesto, te derivan un equipo o te escriben."
                  accion="Activar"
                  onClick={activarAvisos}
                  destacada
                />
              )}

              {ofrecerInstalar && (
                <Fila
                  icono="smartphone"
                  titulo="Instalar la app"
                  detalle={
                    ios
                      ? "En iPhone es necesaria para recibir avisos."
                      : "Queda como una app más, abre al toque y sin barra del navegador."
                  }
                  accion={ios ? "Cómo se hace" : "Instalar"}
                  onClick={instalar}
                  destacada={!ofrecerAvisos}
                />
              )}

              {pasos && (
                <ol className="rounded-xl bg-[#14191B] border border-white/10 p-3.5 text-[13px] text-[#B5C0BB] space-y-2 list-decimal list-inside">
                  <li>Tocá <strong className="text-[#EDF1EF]">Compartir</strong> abajo en Safari (el cuadrito con la flecha).</li>
                  <li>Bajá y elegí <strong className="text-[#EDF1EF]">Agregar a inicio</strong>.</li>
                  <li>Abrí Busco desde el ícono nuevo y volvé acá para activar los avisos.</li>
                </ol>
              )}

              {error && (
                <p className="text-[12.5px] text-[#DE705C] bg-[#DE705C]/10 rounded-lg px-3 py-2">{error}</p>
              )}

              <div className="flex items-center justify-between px-1 pt-0.5">
                <button onClick={posponer} className="text-[12.5px] text-[#808D88] hover:text-[#B5C0BB] py-1">
                  Ahora no
                </button>
                <Link href="/perfil" className="text-[12.5px] text-[#808D88] hover:text-[#B5C0BB] py-1">
                  Ver todos los ajustes
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Fila({
  icono, titulo, detalle, accion, onClick, destacada,
}: {
  icono: "bell" | "smartphone";
  titulo: string;
  detalle: string;
  accion: string;
  onClick: () => void;
  destacada?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-[#14191B] border border-white/10 p-3">
      <span className="w-10 h-10 rounded-xl bg-[#E8A23C]/12 text-[#E8A23C] grid place-items-center flex-none">
        <Icon name={icono} size={19} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[14px] leading-tight">{titulo}</div>
        <div className="text-[12.5px] text-[#808D88] leading-snug mt-0.5">{detalle}</div>
      </div>
      <button
        onClick={onClick}
        className={`rounded-lg px-3.5 py-2 text-[13px] font-bold flex-none transition ${
          destacada
            ? "bg-[#E8A23C] text-[#201404] hover:brightness-105"
            : "bg-white/8 text-[#EDF1EF] hover:bg-white/14"
        }`}
      >
        {accion}
      </button>
    </div>
  );
}
